import gsap from "gsap";
import { pageReady } from "../../utils/page-ready";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { startSvgGlitch } from "../global/anim";
import { wrapInMask } from "../../utils/wrapInMask";

// Text starts revealing shortly after the icon's first glitch burst fires.
const TEXT_REVEAL_STAGGER = 0.15;
const TEXT_REVEAL_DURATION = 0.8;
const TEXT_REVEAL_EASE = "cubic-bezier(0.16, 1, 0.3, 1)"; // ≈ expo.out

// Icon starts fully hidden (0 squares) and builds up path-by-path, glitch-style,
// before handing off to the ambient startSvgGlitch loop.
const ICON_REVEAL_STAGGER = 0.03;
const ICON_REVEAL_DURATION = 0.05;
// Pause after the build-up before the ambient glitch's first tick — without
// it, that first tick fires instantly and reads as the reveal happening twice.
const ICON_AMBIENT_DELAY = 2;

// Icon (svg glitch, see startSvgGlitch) + title (mask reveal from the bottom),
// played together either on page load (data-anim="load", with data-anim-delay)
// or on scroll into view (data-anim="scroll", default).
export default class IconTitle {
  constructor(el) {
    this.el = el;
    this.icon = el.querySelector(".c-icon-title__icon");
    this.iconPaths = this.icon ? [...this.icon.querySelectorAll("path")] : [];
    this.textInner = el.querySelector(".c-icon-title__title > div");

    if (this.iconPaths.length) gsap.set(this.iconPaths, { opacity: 0 });

    if (this.textInner) {
      wrapInMask(this.textInner);
      gsap.set(this.textInner, { y: "110%" });
    }

    const mode = el.dataset.anim || "scroll";
    const delay = parseFloat(el.dataset.animDelay) || 0;

    if (mode === "load") {
      pageReady.ready.then(() => gsap.delayedCall(delay, this.play));
    } else {
      const start = el.dataset.animStart || "top 85%";
      ScrollTrigger.create({ trigger: el, start, onEnter: () => pageReady.ready.then(this.play) });
    }
  }

  play = () => {
    if (this.iconPaths.length) {
      const order = [...this.iconPaths].sort(() => Math.random() - 0.5);
      gsap.to(order, {
        opacity: 1,
        duration: ICON_REVEAL_DURATION,
        ease: "steps(1)",
        stagger: ICON_REVEAL_STAGGER,
        onComplete: () => startSvgGlitch(this.icon, ICON_AMBIENT_DELAY),
      });
    }

    // WAAPI transform animation instead of a GSAP tween: it runs on the compositor,
    // so main-thread work at the same moment (shop card reveals, videos starting)
    // can't stall the slide. The scroll trigger can fire again: slide in only once.
    if (this.textInner && !this.textShown) {
      this.textShown = true;
      const anim = this.textInner.animate(
        [{ transform: "translateY(110%)" }, { transform: "translateY(0)" }],
        {
          duration: TEXT_REVEAL_DURATION * 1000,
          delay: TEXT_REVEAL_STAGGER * 1000,
          easing: TEXT_REVEAL_EASE,
          fill: "both",
        }
      );
      // Hand the end state back to the inline style, then drop the animation.
      anim.finished.then(() => {
        gsap.set(this.textInner, { y: 0 });
        anim.cancel();
      });
    }
  };
}
